import * as THREE from 'three';
import type { Colliders } from './world/Colliders';

/**
 * First-person walking for desktop: the camera is you, at eye height, WASD moves
 * across the yard, the mouse (or a drag) turns the view. Movement eases with
 * `1 - exp(-k·dt)` like everything else; the yard's static colliders push you
 * off the fence, the house, the store and the trees.
 */
const EYE = 1.5;
const WALK = 1.7; // m/s
const RUN = 3.4;
const RADIUS = 0.3;
const TURN = 2.2; // rad/s at full key
const YARD_R = 85; // the land's edge (the fog takes over there)

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _want = new THREE.Vector3();
const _at = new THREE.Vector3();
const _probe = new THREE.Vector3();
/** the yard's boxes are resolved at chest height: a point on the ground is on their floor and gets pushed down, not out */
const PROBE_Y = 0.45;

function keepOut(pos: THREE.Vector3, vel: THREE.Vector3, colliders: Colliders): void {
  _probe.set(pos.x, PROBE_Y, pos.z);
  colliders.resolve(_probe, vel, RADIUS, 0);
  pos.x = _probe.x;
  pos.z = _probe.z;
  vel.y = 0;
}

export class Walker {
  on = false;
  /** feet, on the ground plane */
  readonly pos = new THREE.Vector3();
  /** view direction: yaw 0 looks down +Z; pitch up is positive */
  yaw = 0;
  pitch = 0;
  private vel = new THREE.Vector3();
  private turnVel = 0;
  private stride = 0;
  private bob = 0;

  /** enter walk mode where the camera is now, looking where it looks */
  start(camera: THREE.Camera, lookingAt: THREE.Vector3, colliders: Colliders): void {
    this.on = true;
    this.pos.copy(camera.position).setY(0);
    if (this.pos.length() > YARD_R) this.pos.setLength(YARD_R);
    this.vel.set(0, 0, 0);
    keepOut(this.pos, this.vel, colliders);
    _at.subVectors(lookingAt, camera.position);
    this.yaw = Math.atan2(_at.x, _at.z);
    this.pitch = THREE.MathUtils.clamp(Math.atan2(_at.y, Math.hypot(_at.x, _at.z)), -1.2, 1.2);
  }

  stop(): void {
    this.on = false;
  }

  /** mouse / drag deltas in pixels (right = look right, up = look up) */
  look(dx: number, dy: number, gain = 0.0025): void {
    this.yaw -= dx * gain;
    this.pitch = THREE.MathUtils.clamp(this.pitch - dy * gain, -1.2, 1.2);
  }

  /** one frame of movement; `axis` from the keyboard, then place the camera */
  update(dt: number, axis: { x: number; y: number }, run: boolean, colliders: Colliders, camera: THREE.Camera, lookOut: THREE.Vector3, turn = 0): void {
    // keyboard turning eases in and out so a tap nudges and a hold sweeps
    this.turnVel += (turn * TURN - this.turnVel) * (1 - Math.exp(-10 * dt));
    this.yaw -= this.turnVel * dt;
    _fwd.set(Math.sin(this.yaw), 0, Math.cos(this.yaw));
    _right.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw));
    _want.set(0, 0, 0).addScaledVector(_fwd, axis.y).addScaledVector(_right, axis.x);
    if (_want.lengthSq() > 0) _want.normalize().multiplyScalar(run ? RUN : WALK);
    this.vel.lerp(_want, 1 - Math.exp(-9 * dt));
    this.pos.addScaledVector(this.vel, dt);
    keepOut(this.pos, this.vel, colliders);
    if (this.pos.length() > YARD_R) this.pos.setLength(YARD_R);
    // a little head bob while moving, faster at a run
    const speed = this.vel.length();
    this.stride += dt * (run ? 2.6 : 1.9) * Math.PI * 2 * Math.min(1, speed / WALK);
    this.bob += ((speed > 0.2 ? Math.sin(this.stride) * 0.022 : 0) - this.bob) * (1 - Math.exp(-12 * dt));
    camera.position.set(this.pos.x, EYE + this.bob, this.pos.z);
    const cp = Math.cos(this.pitch);
    lookOut.set(this.pos.x + Math.sin(this.yaw) * cp * 4, EYE + this.bob + Math.sin(this.pitch) * 4, this.pos.z + Math.cos(this.yaw) * cp * 4);
    camera.lookAt(lookOut);
  }

  get moving(): boolean {
    return this.vel.lengthSq() > 0.04;
  }
}
